// pages/UserListPage.tsx
import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {useSearchParams, useNavigate, useParams} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import api from '../api/axios';
import '../styles/UserListPage.css';
import {useClickable} from '../providers/ClickableProvider';
import {Check, ChevronDown, RefreshCw} from 'lucide-react';

type DropdownType = 'animeStatus' | 'season' | 'genres' | 'ageRating' | null;
type DisplayState = 'initial' | 'loading' | 'no-data' | 'no-results' | 'data' | 'error';

interface UserAnimeStatus {
    id: number;
    animeId: number;
    animeTitle: string;
    animeImageUrl: string;
    status: string;
    rating: number | null;
    episodesWatched: number;
    isFavorite: boolean;
    animeEpisodes: number;
    averageRating?: number;
    releaseYear?: number;
    season?: string;
}

interface Genre {
    id: number;
    name: string;
}

interface WatchingStatusCounts {
    [key: string]: number;
}

const UserListPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {nickname} = useParams<{ nickname: string }>();
    const {isAuthenticated, user: currentUser} = useAuth();
    const navigate = useNavigate();
    const isOwnList = !nickname || nickname === currentUser?.nickname;
    const {makeClickable} = useClickable();

    const [error, setError] = useState<string | null>(null);
    const [activeStatus, setActiveStatus] = useState<string>('ALL');
    const [statusCounts, setStatusCounts] = useState<WatchingStatusCounts>({});
    const [genres, setGenres] = useState<Genre[]>([]);
    const [genresLoading, setGenresLoading] = useState(false);
    const [displayState, setDisplayState] = useState<DisplayState>('initial');
    const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);

    // Новое состояние для отслеживания показа спиннера
    const [showSpinner, setShowSpinner] = useState(false);

    // Состояния для пагинации
    const [combinedList, setCombinedList] = useState<UserAnimeStatus[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const spinnerTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);
    const lastRequestRef = useRef<string>(''); // Для предотвращения дублирования запросов

    // Refs для хранения текущих значений
    const filtersRef = useRef({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        userRating: '',
        userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        favorite: '',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    const activeStatusRef = useRef('ALL');
    const isStatusChangingRef = useRef(false); // Новый ref для отслеживания изменения статуса
    const [ageRatings, setAgeRatings] = useState<Array<{ value: string, label: string }>>([]);
    const [ageRatingsLoading, setAgeRatingsLoading] = useState(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
            }
            if (spinnerTimerRef.current) {
                clearTimeout(spinnerTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                openDropdown &&
                dropdownRefs[openDropdown].current &&
                !dropdownRefs[openDropdown].current?.contains(event.target as Node)
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdown]);

    const formatAgeRating = (rating: string): string => {
        if (!rating) return '';

        // Создаем карту для преобразования
        const ratingMap: { [key: string]: string } = {
            'PG_13': 'PG-13',
            'R_17': 'R-17',
            'R_PLUS': 'R+'
        };

        // Если есть в карте - возвращаем преобразованное значение
        if (ratingMap[rating]) {
            return ratingMap[rating];
        }

        // Для G и PG возвращаем как есть
        return rating;
    };

    const dropdownRefs = {
        animeStatus: useRef<HTMLDivElement>(null),
        season: useRef<HTMLDivElement>(null),
        genres: useRef<HTMLDivElement>(null),
        ageRating: useRef<HTMLDivElement>(null)
    };

    // Фильтры
    const [filters, setFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        userRating: '',
        userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        favorite: '',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    const [manualFilters, setManualFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        userRating: '',
        userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        favorite: '',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    const [appliedFilters, setAppliedFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        userRating: '',
        userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        favorite: '',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    // Обновляем refs при изменении состояний
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        activeStatusRef.current = activeStatus;
    }, [activeStatus]);

    const fetchGenres = useCallback(async () => {
        try {
            setGenresLoading(true);
            const response = await api.get('/genres');
            const sortedGenres = response.data.sort((a: Genre, b: Genre) =>
                a.name.localeCompare(b.name)
            );
            setGenres(sortedGenres);
        } catch (error) {
            console.error('Error fetching genres:', error);
        } finally {
            setGenresLoading(false);
        }
    }, []);

    const fetchAgeRatings = useCallback(async () => {
        try {
            setAgeRatingsLoading(true);
            const response = await api.get('/age-ratings'); // Измените URL
            console.log('Age ratings response:', response.data);

            // Преобразуем данные из нового формата
            const formattedData = response.data.map((item: any) => ({
                value: item.code,
                label: item.description
            }));

            console.log('Formatted data:', formattedData);
            setAgeRatings(formattedData);
        } catch (error) {
            console.error('Error fetching age ratings:', error);
        } finally {
            setAgeRatingsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGenres();
        fetchAgeRatings();
    }, [fetchGenres, fetchAgeRatings]);

    const applyFilters = useCallback(async (filterCriteria: typeof filters, status: string, page: number = 0) => {
        if (!isMountedRef.current) return;

        // Создаем уникальный идентификатор запроса
        const requestId = `${JSON.stringify(filterCriteria)}-${status}-${page}`;

        // Пропускаем дублирующие запросы только если это не изменение статуса
        if (lastRequestRef.current === requestId && !isStatusChangingRef.current) {
            console.log('Duplicate request skipped:', requestId);
            return;
        }

        lastRequestRef.current = requestId;
        isStatusChangingRef.current = false; // Сбрасываем флаг изменения статуса

        console.log('Applying filters, page:', page, 'status:', status);

        try {
            if (page === 0) {
                setDisplayState('loading');
                setIsInitialLoading(true);

                // Устанавливаем таймер на 700мс для показа спиннера
                if (spinnerTimerRef.current) {
                    clearTimeout(spinnerTimerRef.current);
                }
                spinnerTimerRef.current = setTimeout(() => {
                    if (isMountedRef.current) {
                        setShowSpinner(true);
                    }
                }, 700);
            } else {
                setIsLoadingMore(true);
            }

            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('size', '15');

            if (filterCriteria.title.trim()) params.append('title', filterCriteria.title.trim());
            if (filterCriteria.animeStatus) params.append('animeStatus', filterCriteria.animeStatus);
            if (filterCriteria.releaseYear) params.append('year', filterCriteria.releaseYear);
            if (filterCriteria.season) params.append('season', filterCriteria.season);
            if (filterCriteria.ageRating) params.append('ageRating', filterCriteria.ageRating);

            if (filterCriteria.genres.length > 0) {
                filterCriteria.genres.forEach(genre => params.append('genre', genre));
            }

            if (filterCriteria.userRating && filterCriteria.userRating !== '') {
                params.append('userRating', filterCriteria.userRating);
                params.append('userRatingCompare', filterCriteria.userRatingCompare);
            }

            if (filterCriteria.globalRating && filterCriteria.globalRating !== '') {
                params.append('globalRating', filterCriteria.globalRating);
                params.append('globalRatingCompare', filterCriteria.globalRatingCompare);
            }

            if (filterCriteria.favorite) params.append('favorite', filterCriteria.favorite);

            if (filterCriteria.episodes && filterCriteria.episodes !== '') {
                params.append('episodes', filterCriteria.episodes);
                params.append('episodesCompare', filterCriteria.episodesCompare);
            }

            // Добавляем статус просмотра
            if (status === 'FAVORITE') {
                params.append('favorite', 'true');
            } else if (status !== 'ALL') {
                params.append('status', status);
            }

            console.log('Request URL:', isOwnList
                ? `/user/list/search?${params.toString()}`
                : `/users/${nickname}/list/search?${params.toString()}`);

            let response;
            if (isOwnList) {
                response = await api.get(`/user/list/search?${params.toString()}`);
            } else if (nickname) {
                response = await api.get(`/users/${nickname}/list/search?${params.toString()}`);
            } else {
                throw new Error('Не указан пользователь');
            }

            if (!isMountedRef.current) return;

            const animeItems = response.data.content || [];
            const hasMoreData = !response.data.last;

            console.log(`Loaded ${animeItems.length} items, hasMore: ${hasMoreData}, page: ${page}`);

            if (page === 0) {
                // Первая загрузка
                setCombinedList(animeItems);
                setHasMore(hasMoreData);
                setCurrentPage(0);

                if (animeItems.length === 0) {
                    const hasAnyFilter = filterCriteria.title ||
                        filterCriteria.animeStatus ||
                        filterCriteria.releaseYear ||
                        filterCriteria.season ||
                        filterCriteria.genres.length > 0 ||
                        filterCriteria.userRating ||
                        filterCriteria.globalRating ||
                        filterCriteria.favorite ||
                        filterCriteria.episodes;

                    setDisplayState(hasAnyFilter ? 'no-results' : 'no-data');
                } else {
                    setDisplayState('data');
                }
            } else {
                // Подгрузка дополнительных данных
                setCombinedList(prev => [...prev, ...animeItems]);
                setHasMore(hasMoreData);
                setCurrentPage(page);
            }

            setError(null);
        } catch (err: any) {
            console.error('Error applying filters:', err);
            if (!isMountedRef.current) return;

            if (page === 0) {
                setDisplayState('error');
                setError('Ошибка при загрузке данных');
            }
        } finally {
            if (isMountedRef.current) {
                // Очищаем таймер спиннера
                if (spinnerTimerRef.current) {
                    clearTimeout(spinnerTimerRef.current);
                    spinnerTimerRef.current = null;
                }
                setShowSpinner(false);
                setIsInitialLoading(false);
                setIsLoadingMore(false);
            }
        }
    }, [isOwnList, nickname]);

    // Функция для загрузки дополнительных данных
    const loadMoreAnime = useCallback(async () => {
        if (isLoadingMore || !hasMore || isInitialLoading) {
            console.log('Skipping loadMore: isLoadingMore=', isLoadingMore, 'hasMore=', hasMore, 'isInitialLoading=', isInitialLoading);
            return;
        }

        const nextPage = currentPage + 1;
        console.log('Loading more anime, next page:', nextPage);
        await applyFilters(filtersRef.current, activeStatusRef.current, nextPage);
    }, [isLoadingMore, hasMore, isInitialLoading, currentPage, applyFilters]);

    // Компонент для бесконечного скролла
    const InfiniteScrollIndicator = () => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!ref.current) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isInitialLoading) {
                        console.log('Infinite scroll triggered');
                        loadMoreAnime();
                    }
                },
                {
                    root: null,
                    rootMargin: '100px',
                    threshold: 0.1
                }
            );

            observer.observe(ref.current);

            return () => {
                observer.disconnect();
            };
        }, [hasMore, isLoadingMore, isInitialLoading, loadMoreAnime]);

        return (
            <>
                <div ref={ref} style={{height: '1px'}}/>

                {isLoadingMore && (
                    <div className="loading-more-indicator">
                        <div className="loading-spinner"></div>
                        <span>Загрузка...</span>
                    </div>
                )}
            </>
        );
    };

    const initializeData = useCallback(async () => {
        if (!isMountedRef.current) return;

        try {
            setIsInitialLoading(true);
            setDisplayState('loading');

            // Устанавливаем таймер на 700мс для показа спиннера
            if (spinnerTimerRef.current) {
                clearTimeout(spinnerTimerRef.current);
            }
            spinnerTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setShowSpinner(true);
                }
            }, 700);

            // Восстанавливаем статус из URL
            const statusFromUrl = searchParams.get('status') || 'ALL';
            setActiveStatus(statusFromUrl);

            // Восстанавливаем фильтры из URL
            const filtersFromUrl = {
                title: searchParams.get('title') || '',
                animeStatus: searchParams.get('animeStatus') || '',
                releaseYear: searchParams.get('releaseYear') || '',
                season: searchParams.get('season') || '',
                genres: searchParams.getAll('genre') || [],
                userRating: searchParams.get('userRating') || '',
                userRatingCompare: (searchParams.get('userRatingCompare') as 'gte' | 'lte' | 'eq') || 'eq',
                globalRating: searchParams.get('globalRating') || '',
                globalRatingCompare: (searchParams.get('globalRatingCompare') as 'gte' | 'lte' | 'eq') || 'eq',
                ageRating: searchParams.get('ageRating') || '',
                favorite: searchParams.get('favorite') || '',
                episodes: searchParams.get('episodes') || '',
                episodesCompare: (searchParams.get('episodesCompare') as 'gte' | 'lte' | 'eq') || 'eq'
            };

            if (filtersFromUrl.title) {
                const cleanTitle = filtersFromUrl.title.replace(/\s+/g, '');
                if (cleanTitle.length < 2) {
                    setError("Введите минимум 2 символа для поиска");
                    setDisplayState('error');
                    return;
                }
            }

            setFilters(filtersFromUrl);
            setManualFilters(filtersFromUrl);
            setAppliedFilters(filtersFromUrl);

            // Загружаем первую страницу
            await applyFilters(filtersFromUrl, statusFromUrl, 0);

        } catch (error) {
            console.error('Error initializing:', error);
            if (isMountedRef.current) {
                setError('Ошибка при загрузке данных');
                setDisplayState('error');
            }
        } finally {
            if (isMountedRef.current) {
                // Очищаем таймер спиннера
                if (spinnerTimerRef.current) {
                    clearTimeout(spinnerTimerRef.current);
                    spinnerTimerRef.current = null;
                }
                setShowSpinner(false);
                setIsInitialLoading(false);
            }
        }
    }, [searchParams, applyFilters]);

    useEffect(() => {
        if (isAuthenticated || !isOwnList) {
            initializeData();
        }
    }, [isAuthenticated, isOwnList, searchParams]); // Убрали initializeData из зависимостей

    useEffect(() => {
        const fetchCounters = async () => {
            if (!isAuthenticated && isOwnList) return;

            try {
                let response;
                if (isOwnList) {
                    response = await api.get('/user/list');
                } else if (nickname) {
                    response = await api.get(`/users/${nickname}/list`);
                } else {
                    return;
                }

                const fullList = Array.isArray(response.data) ? response.data : [];

                const counts: WatchingStatusCounts = {
                    ALL: fullList.length
                };

                fullList.forEach((anime: UserAnimeStatus) => {
                    counts[anime.status] = (counts[anime.status] || 0) + 1;
                    if (anime.isFavorite) {
                        counts['FAVORITE'] = (counts['FAVORITE'] || 0) + 1;
                    }
                });

                setStatusCounts(counts);
            } catch (err) {
                console.error('Error fetching counters:', err);
            }
        };

        fetchCounters();
    }, [isAuthenticated, isOwnList, nickname]);

    const toggleDropdown = (type: DropdownType) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const handleAnimeStatusSelect = (status: string) => {
        setManualFilters(prev => ({...prev, animeStatus: status}));
        setOpenDropdown(null);
    };

    const handleSeasonSelect = (season: string) => {
        setManualFilters(prev => ({...prev, season}));
        setOpenDropdown(null);
    };

    const handleGenreToggle = (genreName: string) => {
        const newGenres = manualFilters.genres.includes(genreName)
            ? manualFilters.genres.filter(g => g !== genreName)
            : [...manualFilters.genres, genreName];
        setManualFilters(prev => ({...prev, genres: newGenres}));
    };

    const getAnimeStatusLabel = () => {
        if (!manualFilters.animeStatus) return 'Все статусы';
        switch (manualFilters.animeStatus) {
            case 'ONGOING':
                return 'Онгоинг';
            case 'COMPLETED':
                return 'Вышел';
            case 'UPCOMING':
                return 'Анонсирован';
            default:
                return manualFilters.animeStatus;
        }
    };

    const getSeasonLabel = () => {
        if (!manualFilters.season) return 'Любой сезон';
        switch (manualFilters.season) {
            case 'WINTER':
                return 'Зима';
            case 'SPRING':
                return 'Весна';
            case 'SUMMER':
                return 'Лето';
            case 'FALL':
                return 'Осень';
            default:
                return manualFilters.season;
        }
    };

    const updateURL = useCallback((newStatus: string, newFilters: typeof filters) => {
        const params = new URLSearchParams();

        // Добавляем статус
        if (newStatus !== 'ALL') {
            params.set('status', newStatus);
        }

        // Добавляем фильтры
        if (newFilters.title) params.set('title', newFilters.title);
        if (newFilters.animeStatus) params.set('animeStatus', newFilters.animeStatus);
        if (newFilters.releaseYear) params.set('releaseYear', newFilters.releaseYear);
        if (newFilters.season) params.set('season', newFilters.season);
        if (newFilters.ageRating) params.set('ageRating', newFilters.ageRating);
        if (newFilters.userRating) {
            params.set('userRating', newFilters.userRating);
            params.set('userRatingCompare', newFilters.userRatingCompare);
        }
        if (newFilters.globalRating) {
            params.set('globalRating', newFilters.globalRating);
            params.set('globalRatingCompare', newFilters.globalRatingCompare);
        }
        if (newFilters.favorite) params.set('favorite', newFilters.favorite);
        if (newFilters.episodes) {
            params.set('episodes', newFilters.episodes);
            params.set('episodesCompare', newFilters.episodesCompare);
        }

        newFilters.genres.forEach(genre => {
            params.append('genre', genre);
        });

        setSearchParams(params);
    }, [setSearchParams]);

    const handleAnimeClick = (animeId: number) => {
        navigate(`/anime/${animeId}`);
    };

    const handleStatusChange = async (status: string) => {
        if (status === activeStatus || isInitialLoading) return;

        const resetFilters = {
            title: '',
            animeStatus: '',
            releaseYear: '',
            season: '',
            genres: [] as string[],
            userRating: '',
            userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
            globalRating: '',
            globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
            favorite: '',
            episodes: '',
            episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
            ageRating: ''
        };

        setManualFilters(resetFilters);
        setFilters(resetFilters);
        setAppliedFilters(resetFilters);
        setActiveStatus(status);
        isStatusChangingRef.current = true; // Устанавливаем флаг изменения статуса
        updateURL(status, resetFilters);
        // Не вызываем applyFilters здесь - он вызовется через useEffect при изменении searchParams
    };

    const handleFilterChange = (key: keyof typeof manualFilters, value: string) => {
        setManualFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSearch = async () => {
        if (isInitialLoading) return;

        if (manualFilters.title) {
            const cleanTitle = manualFilters.title.replace(/\s+/g, '');
            if (cleanTitle.length < 2) {
                setError("Введите минимум 2 символа для поиска");
                setDisplayState('error');
                return;
            }
        }

        setFilters(manualFilters);
        setAppliedFilters(manualFilters);
        updateURL(activeStatus, manualFilters);
        // Не вызываем applyFilters здесь - он вызовется через useEffect при изменении searchParams
    };

    const handleResetFilters = async () => {
        if (isInitialLoading) return;

        const resetFilters = {
            title: '',
            animeStatus: '',
            releaseYear: '',
            season: '',
            genres: [] as string[],
            userRating: '',
            userRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
            globalRating: '',
            globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
            favorite: '',
            episodes: '',
            episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
            ageRating: ''
        };

        setFilters(resetFilters);
        setManualFilters(resetFilters);
        setAppliedFilters(resetFilters);
        updateURL(activeStatus, resetFilters);
        // Не вызываем applyFilters здесь - он вызовется через useEffect при изменении searchParams
    };

    const getApplyButtonTitle = useMemo(() => {
        const hasUserRatingOperator = manualFilters.userRatingCompare !== 'eq';
        const hasGlobalRatingOperator = manualFilters.globalRatingCompare !== 'eq';
        const hasEpisodesOperator = manualFilters.episodesCompare !== 'eq';

        if (hasUserRatingOperator && !manualFilters.userRating) {
            return "Для сравнения оценки введите число или верните фильтр в исходное состояние (=)";
        }

        if (hasGlobalRatingOperator && !manualFilters.globalRating) {
            return "Для сравнения общей оценки введите число или верните фильтр в исходное состояние (=)";
        }

        if (hasEpisodesOperator && !manualFilters.episodes) {
            return "Для сравнения эпизодов введите число или верните фильтр в исходное состояние (=)";
        }

        const filtersChanged = JSON.stringify(manualFilters) !== JSON.stringify(appliedFilters);

        if (!filtersChanged) {
            return "Нет изменений в фильтрах";
        }

        return "Применить фильтры";
    }, [manualFilters, appliedFilters]);

    const hasFilterChanges = useMemo(() => {
        const filtersChanged = JSON.stringify(manualFilters) !== JSON.stringify(appliedFilters);

        if (!filtersChanged) return false;

        const hasUserRatingOperator = manualFilters.userRatingCompare !== 'eq';
        const hasGlobalRatingOperator = manualFilters.globalRatingCompare !== 'eq';
        const hasEpisodesOperator = manualFilters.episodesCompare !== 'eq';

        if ((hasUserRatingOperator && !manualFilters.userRating) ||
            (hasGlobalRatingOperator && !manualFilters.globalRating) ||
            (hasEpisodesOperator && !manualFilters.episodes)) {
            return false;
        }

        if (manualFilters.userRating) {
            const rating = parseFloat(manualFilters.userRating);
            if (isNaN(rating) || rating < 1 || rating > 10) {
                return false;
            }
        }

        if (manualFilters.globalRating) {
            const rating = parseFloat(manualFilters.globalRating);
            if (isNaN(rating) || rating < 1 || rating > 10) {
                return false;
            }
        }

        if (manualFilters.episodes) {
            const episodes = parseInt(manualFilters.episodes);
            if (isNaN(episodes) || episodes < 1 || episodes > 1000) {
                return false;
            }
        }

        if (manualFilters.ageRating) {
            const validAgeRatings = ageRatings.map(r => r.value);
            if (!validAgeRatings.includes(manualFilters.ageRating) && manualFilters.ageRating !== '') {
                return false;
            }
        }

        return true;
    }, [manualFilters, appliedFilters]);

    const hasAppliedFilters = useMemo(() => {
        const hasFiltersFromApplied =
            appliedFilters.title ||
            appliedFilters.animeStatus ||
            appliedFilters.releaseYear ||
            appliedFilters.season ||
            appliedFilters.genres.length > 0 ||
            appliedFilters.userRating ||
            appliedFilters.globalRating ||
            appliedFilters.favorite ||
            appliedFilters.episodes ||
            appliedFilters.ageRating;

        return hasFiltersFromApplied;
    }, [appliedFilters]);

    const getStatusClass = (status: string): string => {
        const classMap: { [key: string]: string } = {
            'WATCHING': 'status-watching',
            'COMPLETED': 'status-completed',
            'PLANNED': 'status-planned',
            'DROPPED': 'status-dropped',
            'REWATCHING': 'status-rewatching',
            'FAVORITE': 'status-favorite'
        };
        return classMap[status] || '';
    };

    const getStatusDisplayName = (status: string): string => {
        const displayNames: { [key: string]: string } = {
            'ALL': 'Все',
            'WATCHING': 'Смотрю',
            'COMPLETED': 'Просмотрено',
            'PLANNED': 'Запланировано',
            'DROPPED': 'Брошено',
            'REWATCHING': 'Пересматриваю',
            'FAVORITE': 'Любимое'
        };
        return displayNames[status] || status;
    };

    const renderTable = () => {
        if (combinedList.length === 0) return null;

        return (
            <>
                <div className={`table-headers ${activeStatus === 'ALL' ? 'all-view' : ''}`}>
                    <div className="header-number">№</div>
                    <div className="header-image">img</div>
                    <div className="header-title">Название</div>
                    {activeStatus === 'ALL' && <div className="header-status">Статус</div>}
                    <div className="header-rating">Оценка</div>
                    <div className="header-global-rating">Общая оценка</div>
                    <div className="header-episodes">Эпизоды</div>
                </div>

                <div className="anime-items">
                    {combinedList.map((anime, index) => (
                        <div key={`${anime.id}-${index}`}
                             className={`anime-item ${activeStatus === 'ALL' ? 'all-view' : ''}`}>
                            <div className="item-number">{index + 1}</div>
                            <div className="item-image">
                                <div
                                    {...makeClickable(`/anime/${anime.animeId}`, (e) => {
                                        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                            handleAnimeClick(anime.animeId);
                                        }
                                    })}
                                    style={{cursor: 'pointer', display: 'inline-block'}}
                                >
                                    <img
                                        src={anime.animeImageUrl}
                                        alt={anime.animeTitle}
                                        className="anime-image"
                                        onError={(e) => {
                                            e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=50&h=70&fit=crop";
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="item-title">
                                <span
                                    className="anime-name"
                                    {...makeClickable(`/anime/${anime.animeId}`, (e) => {
                                        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                            handleAnimeClick(anime.animeId);
                                        }
                                    })}
                                    style={{cursor: 'pointer'}}
                                >
                                    {anime.animeTitle}
                                </span>
                            </div>
                            {activeStatus === 'ALL' && (
                                <div className={`item-status ${getStatusClass(anime.status)}`}>
                                    {getStatusDisplayName(anime.status)}
                                </div>
                            )}
                            <div className="item-rating">
                                {anime.rating || '0'}
                            </div>
                            <div className="item-global-rating">
                                {anime.averageRating ? anime.averageRating.toFixed(1) : '0'}
                            </div>
                            <div className="item-episodes">
                                {anime.episodesWatched}/{anime.animeEpisodes || '?'}
                            </div>
                        </div>
                    ))}

                    {/* Индикатор бесконечного скролла */}
                    <InfiniteScrollIndicator/>
                </div>
            </>
        );
    };

    if (!isAuthenticated && isOwnList) {
        return (
            <div className="user-list-page">
                <div className="auth-required">
                    <h2>Требуется авторизация</h2>
                    <p>Пожалуйста, войдите в систему для просмотра вашего списка аниме.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="user-list-page">
            <div className="user-list-container-wrapper">
                <div className="user-list-container">
                    <div className="anime-list-section">
                        <div className="list-navigation">
                            <div className="status-tabs">
                                {[
                                    {key: 'ALL', name: 'Все'},
                                    {key: 'WATCHING', name: 'Смотрю'},
                                    {key: 'COMPLETED', name: 'Просмотрено'},
                                    {key: 'PLANNED', name: 'Запланировано'},
                                    {key: 'DROPPED', name: 'Брошено'},
                                    {key: 'REWATCHING', name: 'Пересматриваю'},
                                    {key: 'FAVORITE', name: 'Любимое'}
                                ].map(status => (
                                    <button
                                        key={status.key}
                                        className={`status-tab ${activeStatus === status.key ? 'active' : ''}`}
                                        onClick={() => handleStatusChange(status.key)}
                                        disabled={isInitialLoading}
                                    >
                                        <span className="status-name">{status.name}</span>
                                        <span className="status-count">({statusCounts[status.key] || 0})</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="anime-list" style={{position: 'relative', minHeight: '200px'}}>
                            {showSpinner && (
                                <div className="loading-overlay">
                                    <div className="loading-spinner-large"></div>
                                    <span>Загрузка...</span>
                                </div>
                            )}

                            {displayState === 'no-data' && (
                                <div className="no-results">
                                    <div style={{textAlign: 'center'}}>
                                        Нет аниме в вашем списке
                                    </div>
                                </div>
                            )}

                            {displayState === 'no-results' && (
                                <div className="no-results">
                                    <div style={{textAlign: 'center'}}>
                                        Аниме не найдено
                                    </div>
                                </div>
                            )}

                            {displayState === 'error' && (
                                <div className="error-user-list-page">
                                    {error || 'Произошла ошибка'}
                                </div>
                            )}

                            {displayState === 'data' && (
                                <>
                                    {combinedList.length === 0 ? (
                                        <div className="no-results">
                                            <div style={{textAlign: 'center'}}>
                                                Нет данных для отображения
                                            </div>
                                        </div>
                                    ) : (
                                        renderTable()
                                    )}
                                </>
                            )}

                            {displayState === 'initial' && (
                                <div style={{height: '200px'}}></div>
                            )}
                        </div>
                    </div>

                    {/* Остальная часть кода с фильтрами остается без изменений */}
                    <div className="filters-panel">
                        <h3 className="filters-title">Фильтры</h3>

                        <div className="filter-group">
                            <label className="filter-label">Название</label>
                            <input
                                type="text"
                                value={manualFilters.title}
                                onChange={(e) => handleFilterChange('title', e.target.value)}
                                className="filter-input"
                                placeholder="Поиск..."
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Статус аниме</label>
                            <div className="dropdown-container-list" ref={dropdownRefs.animeStatus}>
                                <button
                                    className={`dropdown-button ${openDropdown === 'animeStatus' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('animeStatus')}
                                >
                                    <span>{getAnimeStatusLabel()}</span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'animeStatus' && (
                                    <div className="dropdown-menu-list">
                                        <div className="dropdown-option selected"
                                             onClick={() => handleAnimeStatusSelect('')}>
                                            Все статусы
                                        </div>
                                        <div className="dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('ONGOING')}>
                                            Онгоинг
                                        </div>
                                        <div className="dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('COMPLETED')}>
                                            Вышел
                                        </div>
                                        <div className="dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('UPCOMING')}>
                                            Анонсирован
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Год выпуска</label>
                            <input
                                type="number"
                                value={manualFilters.releaseYear}
                                onChange={(e) => handleFilterChange('releaseYear', e.target.value)}
                                className="filter-input"
                                placeholder="Год"
                                min="1950"
                                max={new Date().getFullYear() + 2}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Сезон</label>
                            <div className="dropdown-container-list" ref={dropdownRefs.season}>
                                <button
                                    className={`dropdown-button ${openDropdown === 'season' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('season')}
                                >
                                    <span>{getSeasonLabel()}</span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'season' && (
                                    <div className="dropdown-menu-list">
                                        <div className="dropdown-option selected"
                                             onClick={() => handleSeasonSelect('')}>
                                            Любой сезон
                                        </div>
                                        <div className="dropdown-option" onClick={() => handleSeasonSelect('WINTER')}>
                                            Зима
                                        </div>
                                        <div className="dropdown-option" onClick={() => handleSeasonSelect('SPRING')}>
                                            Весна
                                        </div>
                                        <div className="dropdown-option" onClick={() => handleSeasonSelect('SUMMER')}>
                                            Лето
                                        </div>
                                        <div className="dropdown-option" onClick={() => handleSeasonSelect('FALL')}>
                                            Осень
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Жанры</label>
                            <div className="dropdown-container-list" ref={dropdownRefs.genres}>
                                <button
                                    className={`dropdown-button ${openDropdown === 'genres' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('genres')}
                                >
                                    <span>
                                        {manualFilters.genres.length === 0
                                            ? 'Все жанры'
                                            : `Выбрано: ${manualFilters.genres.length}`}
                                    </span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'genres' && (
                                    <div className="dropdown-menu-list">
                                        <div className="genres-dropdown-content">
                                            {genresLoading ? (
                                                <div style={{
                                                    color: 'rgba(255,255,255,0.7)',
                                                    textAlign: 'center',
                                                    padding: '10px'
                                                }}>
                                                    Загрузка жанров...
                                                </div>
                                            ) : (
                                                genres.map(genre => (
                                                    <div
                                                        key={genre.id}
                                                        className={`genre-item ${manualFilters.genres.includes(genre.name) ? 'selected' : ''}`}
                                                        onClick={() => handleGenreToggle(genre.name)}
                                                    >
                                                        <span className="genre-item-text">{genre.name}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Рейтинг</label>
                            <div className="rating-dropdown-container">
                                <button
                                    className={`rating-dropdown-button ${openDropdown === 'ageRating' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('ageRating')}
                                >
                                    <span>
                                        {manualFilters.ageRating === ''
                                            ? 'Все рейтинги'
                                            : formatAgeRating(manualFilters.ageRating)} {/* Используем форматирование */}
                                    </span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'ageRating' && (
                                    <div className="rating-dropdown-menu">
                                        <div
                                            className={`rating-dropdown-option ${manualFilters.ageRating === '' ? 'selected' : ''}`}
                                            onClick={() => {
                                                handleFilterChange('ageRating', '');
                                                setOpenDropdown(null);
                                            }}
                                        >
                                            Все рейтинги
                                        </div>
                                        {ageRatingsLoading ? (
                                            <div className="rating-dropdown-option"
                                                 style={{color: 'rgba(255,255,255,0.7)', textAlign: 'center'}}>
                                                Загрузка...
                                            </div>
                                        ) : (
                                            ageRatings.map(rating => (
                                                <div
                                                    key={rating.value}
                                                    className={`rating-dropdown-option ${manualFilters.ageRating === rating.value ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleFilterChange('ageRating', rating.value);
                                                        setOpenDropdown(null);
                                                    }}
                                                >
                                                    {formatAgeRating(rating.value)} {/* Форматируем здесь тоже */}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Оценка</label>
                            <div className="compact-filter-with-select">
                                <select
                                    value={manualFilters.userRatingCompare}
                                    onChange={(e) => handleFilterChange('userRatingCompare', e.target.value as 'eq' | 'gte' | 'lte')}
                                    className="comparison-select"
                                >
                                    <option value="eq">=</option>
                                    <option value="gte">≥</option>
                                    <option value="lte">≤</option>
                                </select>
                                <input
                                    type="number"
                                    value={manualFilters.userRating}
                                    onChange={(e) => handleFilterChange('userRating', e.target.value)}
                                    className="compact-input-with-select"
                                    placeholder="1-10"
                                    min="1"
                                    max="10"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Общая оценка</label>
                            <div className="compact-filter-with-select">
                                <select
                                    value={manualFilters.globalRatingCompare}
                                    onChange={(e) => handleFilterChange('globalRatingCompare', e.target.value as 'eq' | 'gte' | 'lte')}
                                    className="comparison-select"
                                >
                                    <option value="eq">=</option>
                                    <option value="gte">≥</option>
                                    <option value="lte">≤</option>
                                </select>
                                <input
                                    type="number"
                                    value={manualFilters.globalRating}
                                    onChange={(e) => handleFilterChange('globalRating', e.target.value)}
                                    className="compact-input-with-select"
                                    placeholder="1-10"
                                    min="1"
                                    max="10"
                                    step="0.1"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Эпизоды</label>
                            <div className="compact-filter-with-select">
                                <select
                                    value={manualFilters.episodesCompare}
                                    onChange={(e) => handleFilterChange('episodesCompare', e.target.value as 'eq' | 'gte' | 'lte')}
                                    className="comparison-select"
                                >
                                    <option value="eq">=</option>
                                    <option value="gte">≥</option>
                                    <option value="lte">≤</option>
                                </select>
                                <input
                                    type="number"
                                    value={manualFilters.episodes}
                                    onChange={(e) => handleFilterChange('episodes', e.target.value)}
                                    className="compact-input-with-select"
                                    placeholder="1-1000"
                                    min="1"
                                    max="1000"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Любимое</label>
                            <div style={{display: 'flex', gap: '8px', marginTop: '4px'}}>
                                <button
                                    className={`favorite-btn ${manualFilters.favorite === 'true' ? 'active' : ''}`}
                                    onClick={() => handleFilterChange('favorite', manualFilters.favorite === 'true' ? '' : 'true')}
                                    style={{flex: 1}}
                                >
                                    Да
                                </button>
                                <button
                                    className={`favorite-btn ${manualFilters.favorite === 'false' ? 'active' : ''}`}
                                    onClick={() => handleFilterChange('favorite', manualFilters.favorite === 'false' ? '' : 'false')}
                                    style={{flex: 1}}
                                >
                                    Нет
                                </button>
                            </div>
                        </div>

                        <div className="filter-actions">
                            <button
                                onClick={handleSearch}
                                className="filter-btn apply-btn"
                                disabled={!hasFilterChanges || isInitialLoading}
                                title={getApplyButtonTitle}
                            >
                                <Check className="filter-btn-icon" size={16}/>
                            </button>
                            <button
                                onClick={handleResetFilters}
                                className="filter-btn reset-btn"
                                disabled={!hasAppliedFilters || isInitialLoading}
                                title={!hasAppliedFilters ? "Нет примененных фильтров" : "Сбросить фильтры"}
                            >
                                <RefreshCw className="filter-btn-icon" size={16}/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserListPage;